import { useState, useEffect, useRef } from "react";
import { BASE_URL } from "../../config";
import { showSuccess, showError, showConfirm } from "../../utils/dialog";
import useAuth from "../../hooks/useAuth";
import { FaPen, FaTimes } from "react-icons/fa";

const AddSkillForm = ({ onClose }) => {

  const [skillData, setSkillData] = useState([]);
  const auth = useAuth();

  const [selectedSkills, setSelectedSkills] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const containerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleSkill = (skill) => {
    if (selectedSkills.includes(skill)) {
      setSelectedSkills(selectedSkills.filter((s) => s !== skill));
    } else {
      setSelectedSkills([...selectedSkills, skill]);
    }
  };

  const removeSkill = (skill) => {
    setSelectedSkills(selectedSkills.filter((s) => s !== skill));
  };

  // Load available skills from backend
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/personnel/skills`, { credentials: 'include' });
        const j = await res.json().catch(() => ({}));
        const rows = Array.isArray(j.data) ? j.data : (Array.isArray(j) ? j : []);
        if (!cancelled) setSkillData(rows.map(r => ({ id: r.skill_id ?? r.id, name: r.name ?? r })));
      } catch (e) {
        console.error('Failed to load skills', e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const accountId = (typeof window !== 'undefined') ? (localStorage.getItem('accountId') || auth?.accountId) : auth?.accountId;
      if (!accountId) return showError('Account not found');

      if (!selectedSkills || selectedSkills.length === 0) return showError('Select at least one skill');

      // Map selected skill names back to ids using skillData
      const selectedIds = selectedSkills.map(name => {
        const found = skillData.find(s => s.name === name || s.skill === name || s.skill_name === name);
        return found ? Number(found.id) : null;
      }).filter(Boolean);

      if (selectedIds.length === 0) return showError('Selected skills do not match available skills');

      // Confirm user intent
      if (!(await showConfirm('Send request to office/admin for review?'))) return;

      // Prepare human-friendly change description
      const addedNames = selectedSkills.join(', ');
      const changes = { 'Skills to add': `Add: ${addedNames}` };

      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/api/temp-staff/request-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({ accountId: Number(accountId), changes, attachments: [] })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.success) {
        console.warn('Request-change failed', j);
        return showError(j?.message || 'Failed to submit change request');
      }

      showSuccess('Your request has been sent to our team for review and update.');
      onClose && onClose();
    } catch (err) {
      console.error('AddSkill error', err);
      showError('Failed to add skills');
    }
  };

  return (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative">
      {/* Close button */}
      <button 
        onClick={onClose} 
        className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
       >
        ✖
      </button>

      {/* Header */}
      <div className="flex">
        <FaPen className="text-buttonRed text-lg w-5 h-5 mt-1 mr-2"/>
        <h2 className="text-xl text-formTitleText font-semibold mb-4">Edit Skills</h2>
      </div>
      
      <div className="h-[1px] bg-[#DC1F004D]"></div>

      {/* Form */}
          <form className="flex-1 p-6" onSubmit={handleSubmit}>
        <div className="relative mb-10" ref={containerRef}>
          <label className="block text-sm text-textGray mb-2">Skills</label>
          <div
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full border rounded-lg px-3 py-2 flex flex-wrap gap-2 cursor-pointer min-h-[40px]"
          >
            {selectedSkills.length === 0 && (
              <span className="text-gray-400">Select skill</span>
            )}

            {selectedSkills.map((skill) => (
              <span
                key={skill}
                className="bg-red-100 text-red-600 px-2 py-1 rounded-full flex items-center gap-1"
              >
                {skill}
                <FaTimes
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSkill(skill);
                  }}
                />
              </span>
            ))}
          </div>

          {showDropdown && (
            <div className="absolute left-0 top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
              {skillData.map((skill) => (
                <div
                  key={skill.id}
                  className={`px-3 py-2 cursor-pointer hover:bg-red-100 ${
                    selectedSkills.includes(skill.name) ? "bg-red-50" : ""
                  }`}
                  onClick={() => toggleSkill(skill.name)}
                >
                  {skill.name}
                </div>
              ))}
            </div>
          )}
        </div>  

        <div className="flex items-center justify-between gap-4 mb-2">
          <button
            onClick={onClose}
            className="w-full border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-4 py-2 rounded-full"
          >
          Cancel
          </button>
          <button
            type="submit"
            className="w-full bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full"
          >
          Request Changes
          </button>
        </div>
        <p className="ml-4 py-1 text-xs">Requested change will be sent to our team to review and update.</p>
      </form>
    </div>
  </div>
  )
};

export default AddSkillForm;